/**
 * Per-scope dock conversation stores (TASK-713).
 *
 * One store instance per entity-qualified scope key, created on demand and
 * cached for the session. Persistence: sessionStorage under
 * `kielis-dock:{accountId}:{scopeType}:{entityId}` (TASK-710 identity
 * decision) — parity with GenAIChat's reload-survival; resume/retention is
 * TASK-719's. All keys are purged on logout/account switch
 * (purgeDockScopeStorage, wired in TASK-710).
 */

import {
  createChatStore,
  dockScopeStorageKey,
  type ChatScope,
} from './createChatStore';
import type { Artifact, ChatAction, ChatSource } from '../types/chat.types';
import type { BriefUpdateItem, ProposalActionItem } from '../types/prepChat.types';
import type { ExtractionProposalItem } from './kycBridge';
import type { ArtifactCardProps } from '../components/canvas/widgets/ArtifactCard';

export interface DockSideChannels extends Record<string, unknown[]> {
  artifacts: Artifact[];
  actions: ChatAction[];
  sources: ChatSource[];
  /**
   * TASK-768: creation results (`widget` chunks with `component: 'ArtifactCard'`)
   * stay IN this conversation as cards — the same fix TASK-742 made in the rail,
   * which the dock was missed on. Unlike the rail, which keeps its cards in
   * component state, the dock attaches them to the MESSAGE: the dock persists its
   * transcript and switches scope, and component state survives neither.
   */
  artifactCards: ArtifactCardProps[];
  // TASK-715 — meeting scope. Empty for every other scope; one store shape
  // keeps the factory simple.
  briefUpdates: BriefUpdateItem[];
  proposalActions: ProposalActionItem[];
  actionProposals: ProposalActionItem[];
  // TASK-716 — kyc scope.
  extractionProposals: ExtractionProposalItem[];
}

type DockChatStore = ReturnType<typeof makeStore>;

function makeStore(storageKey: string, scope: ChatScope) {
  return createChatStore<DockSideChannels>({
    name: `DockChat:${storageKey}`,
    slots: [
      'artifacts',
      'actions',
      'sources',
      'artifactCards',
      'briefUpdates',
      'proposalActions',
      'actionProposals',
      'extractionProposals',
    ],
    storageKey,
    // TASK-719 persistence boundary (conservative pending security sign-off):
    // KYC transcript/proposal CONTENT never touches sessionStorage — the kyc
    // scope persists the conversationId only and rehydrates via the backend
    // conversation store.
    //
    // TASK-795 / DEC-7: the `dashboard` scope reaches this line for the first
    // time and persists, deliberately. TASK-719's caution was taken about KYC
    // content on the KYC surface; a dashboard conversation is no more sensitive
    // than the client-route thread that already persists beside it, and
    // "reload-survival everywhere except the dashboard" is a line with no
    // principle behind it. Bounded by what bounds every other scope:
    // sessionStorage, purged on logout and account switch (purgeDockScopeStorage).
    persistMessages: scope.scopeType !== 'kyc',
  });
}

const cache = new Map<string, DockChatStore>();

export function getDockChatStore(accountId: string, scope: ChatScope): DockChatStore {
  const key = dockScopeStorageKey(accountId, scope);
  let store = cache.get(key);
  if (!store) {
    store = makeStore(key, scope);
    cache.set(key, store);
  }
  return store;
}
